import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { attendanceService, expenseService, projectService, salaryService, purchaseOrderService, employeeService, taskAssignmentService } from '../services/api'
import TablePagination from '../components/TablePagination'
import useResponsivePageSize from '../hooks/useResponsivePageSize'
import { formatCurrency } from '../utils/currency'
import { formatDate, formatDateForInput } from '../utils/dateFormat'
import useModalFeedback from '../hooks/useModalFeedback'
import useRolePermissions from '../hooks/useRolePermissions'
import TruncatedText from '../components/TruncatedText'
import CollapsibleFilters from '../components/CollapsibleFilters'
import MultiSelectFilterDropdown from '../components/MultiSelectFilterDropdown'
import { getBaseUrl } from '../utils/apiConfig'

export default function Payroll() {
  const { showMessage, showConfirm, modalElement } = useModalFeedback()
  const { isViewer, canMutate } = useRolePermissions()
  const [salaries, setSalaries] = useState([])
  const [attendances, setAttendances] = useState([])
  const [projects, setProjects] = useState([])
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [employees, setEmployees] = useState([])
  const [activeTab, setActiveTab] = useState('attendance') // 'attendance', 'salary', or 'task-assignment'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showSalaryEditModal, setShowSalaryEditModal] = useState(false)
  const [selectedSalaryRow, setSelectedSalaryRow] = useState(null)
  const [salaryEditForm, setSalaryEditForm] = useState({
    allowance: 0,
    employeer_epf: 0,
    remarks: '',
    status: 'Unpaid'
  })
  const [showAttendanceEditModal, setShowAttendanceEditModal] = useState(false)
  const [selectedAttendance, setSelectedAttendance] = useState(null)
  const [viewingAttendanceFile, setViewingAttendanceFile] = useState(null)
  const [attendanceEditForm, setAttendanceEditForm] = useState({
    project_id: '',
    po_id: '',
    submission_date: '',
    clock_in: '08:00',
    clock_out: '17:00',
    normal_hours: '',
    ot_hours: '',
    type: 'WD',
    remarks: ''
  })
  const [salaryPage, setSalaryPage] = useState(1)
  const [attendancePage, setAttendancePage] = useState(1)
  const [salaryFilters, setSalaryFilters] = useState({
    years: [],
    months: [],
    employeeNames: [],
    statuses: []
  })
  const [attendanceFilters, setAttendanceFilters] = useState({
    years: [],
    months: [],
    employeeNames: [],
    projectIds: []
  })

  // Task Assignment states
  const [taskAssignments, setTaskAssignments] = useState([])
  const [selectedTeam, setSelectedTeam] = useState('')
  const [selectedPO, setSelectedPO] = useState('')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedFromDate, setSelectedFromDate] = useState(null)
  const [selectedToDate, setSelectedToDate] = useState(null)
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [modalPO, setModalPO] = useState('') // PO selection for booking modal (separate from filter)
  const [modalTeam, setModalTeam] = useState('') // Team selection for booking modal (separate from filter)
  const [editingAssignment, setEditingAssignment] = useState(null) // For editing existing assignments
  const [currentView, setCurrentView] = useState('month') // 'month' or 'week'
  const renderModal = (content) => {
    if (typeof document === 'undefined') return null
    return createPortal(content, document.body)
  }

  const API_BASE_URL = getBaseUrl()

  const parseAttachmentList = (attachmentsValue) => {
    if (!attachmentsValue) return []

    if (Array.isArray(attachmentsValue)) {
      return attachmentsValue
        .map((item) => (typeof item === 'string' ? item : (item?.path || item)))
        .filter(Boolean)
    }

    if (typeof attachmentsValue === 'string') {
      const trimmed = attachmentsValue.trim()
      if (!trimmed) return []

      try {
        const parsed = JSON.parse(trimmed)
        return parseAttachmentList(parsed)
      } catch {
        return trimmed.split(',').map((item) => item.trim()).filter(Boolean)
      }
    }

    if (typeof attachmentsValue === 'object') {
      return Object.values(attachmentsValue).map((item) => (typeof item === 'string' ? item : item?.path)).filter(Boolean)
    }

    return []
  }

  const resolveAttachmentUrl = (rawPath) => {
    const normalizedRaw = String(rawPath || '').trim()
    if (!normalizedRaw) return ''
    if (normalizedRaw.startsWith('http://') || normalizedRaw.startsWith('https://')) {
      return normalizedRaw
    }

    const slashPath = normalizedRaw.replace(/\\/g, '/')
    if (slashPath.startsWith('/')) {
      return `${API_BASE_URL}${slashPath}`
    }
    return `${API_BASE_URL}/${slashPath}`
  }

  const getFileExtension = (filePath) => {
    return filePath?.split('.').pop()?.toLowerCase() || ''
  }

  const getAttachmentIcon = (ext) => {
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️'
    if (ext === 'pdf') return '📄'
    return '📁'
  }

  const getAttachmentTooltip = (ext) => {
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'View Image'
    if (ext === 'pdf') return 'View Document'
    return ext ? `View ${ext.toUpperCase()} file` : 'View Attachment'
  }

  const openAttendanceFileViewer = (attendance) => {
    const attachments = parseAttachmentList(attendance.attachment)
    if (!attachments.length) return
    const firstFile = attachments[0]
    const fullUrl = resolveAttachmentUrl(firstFile)
    if (fullUrl) {
      setViewingAttendanceFile(fullUrl)
    }
  }

  const closeAttendanceFileViewer = () => {
    setViewingAttendanceFile(null)
  }

  const downloadFile = async (filePath, fileName) => {
    try {
      const fullUrl = filePath.startsWith('http') ? filePath : `${API_BASE_URL}${filePath}`
      const response = await fetch(fullUrl)
      if (!response.ok) throw new Error('Download failed')

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || fullUrl.split('/').pop() || 'file'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      showMessage('Failed to download file', 'Error')
    }
  }

  const { containerRef: salaryTableRef, rowsPerPage: salaryRowsPerPage } = useResponsivePageSize({ reservedHeight: 420, footerHeight: 24, rowHeight: 52, minRows: 5, maxRows: 12 })
  const { containerRef: attendanceTableRef, rowsPerPage: attendanceRowsPerPage } = useResponsivePageSize({ reservedHeight: 420, footerHeight: 24, rowHeight: 52, minRows: 5, maxRows: 12 })

  const monthFormatter = new Intl.DateTimeFormat(undefined, { month: 'long' })

  const getMonthLabel = (monthValue) => {
    const monthNumber = Number(monthValue)
    if (!Number.isFinite(monthNumber) || monthNumber < 1 || monthNumber > 12) return '-'
    return monthFormatter.format(new Date(2000, monthNumber - 1, 1))
  }

  // Fetch attendance data on mount and when tab changes
  useEffect(() => {
    fetchProjects()
    fetchPurchaseOrders()
  }, [])

  useEffect(() => {
    if (activeTab === 'attendance') {
      fetchAttendances()
    } else if (activeTab === 'salary') {
      fetchSalaries()
    } else if (activeTab === 'task-assignment') {
      fetchPurchaseOrders()
      fetchEmployees()
      fetchTaskAssignments()
    }
  }, [activeTab])

  useEffect(() => {
    setSalaryPage(1)
    setAttendancePage(1)
  }, [activeTab])

  useEffect(() => {
    const triggerLayoutRecalc = () => {
      window.dispatchEvent(new Event('layout:filters-toggle'))
    }

    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(triggerLayoutRecalc)
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [activeTab, salaries.length, attendances.length])

  const fetchProjects = async () => {
    try {
      const response = await projectService.getAll()
      setProjects(response.data.data || [])
    } catch (err) {
      console.error('Error fetching projects:', err)
      setProjects([])
    }
  }

  const fetchAttendances = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await attendanceService.getAll()
      // API returns { success: true, data: [...] }
      setAttendances(response.data.data || [])
    } catch (err) {
      console.error('Error fetching attendances:', err)
      setError(err.message || 'Failed to fetch timesheet records')
      setAttendances([])
    } finally {
      setLoading(false)
    }
  }

  const fetchSalaries = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await salaryService.getSummary()
      const summaryData = response.data.data || []

      if (summaryData.length > 0) {
        setSalaries(normalizeSalaryRows(summaryData))
      } else {
        const [attendanceResponse, expenseResponse] = await Promise.all([
          attendanceService.getAll(),
          expenseService.getAll()
        ])
        const attendanceData = attendanceResponse.data.data || []
        const expenseData = expenseResponse.data.data || []
        setSalaries(normalizeSalaryRows(buildSalarySummaryFromAttendance(attendanceData, expenseData)))
      }
    } catch (err) {
      try {
        // Fallback when /salaries/summary is not available yet on running backend
        const [attendanceResponse, expenseResponse] = await Promise.all([
          attendanceService.getAll(),
          expenseService.getAll()
        ])
        const attendanceData = attendanceResponse.data.data || []
        const expenseData = expenseResponse.data.data || []
        setSalaries(normalizeSalaryRows(buildSalarySummaryFromAttendance(attendanceData, expenseData)))
      } catch (fallbackErr) {
        console.error('Error fetching salaries:', err)
        console.error('Fallback attendance summary failed:', fallbackErr)
        setError(fallbackErr.message || 'Failed to fetch salary records')
        setSalaries([])
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchPurchaseOrders = async () => {
    try {
      const response = await purchaseOrderService.getAll()
      setPurchaseOrders(response.data.data || [])
    } catch (err) {
      console.error('Error fetching purchase orders:', err)
      setPurchaseOrders([])
    }
  }

  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getAll()
      setEmployees(response.data.data || [])
    } catch (err) {
      console.error('Error fetching employees:', err)
      setEmployees([])
    }
  }

  const fetchTaskAssignments = async () => {
    try {
      setLoading(true)
      // Fetch task assignments from database
      const response = await taskAssignmentService.getAll()
      const assignments = response.data.data || []


      // Transform DB data to match expected format
      const transformedAssignments = assignments.map(assignment => ({
        id: assignment.id,
        po_number: assignment.po_number,
        po_id: assignment.po_id,
        team: assignment.team,
        from_date: assignment.from_date,
        to_date: assignment.to_date,
        status: assignment.status,
        notes: assignment.notes,
        created_at: assignment.created_at,
        updated_at: assignment.updated_at
      }))

      setTaskAssignments(transformedAssignments)
    } catch (err) {
      console.error('❌ Error fetching task assignments:', err)
      setTaskAssignments([])
      showMessage(err.response?.data?.message || 'Failed to load task assignments. Please ensure the database table exists.', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const normalizeSalaryRows = (rows) => {
    const normalizedRows = (rows || []).map((row) => {
      const periodParts = String(row?.period || '').split('-')
      const parsedYear = Number(periodParts[0])
      const parsedMonth = Number(periodParts[1])

      return {
        ...row,
        year: Number(row?.year) || (Number.isFinite(parsedYear) ? parsedYear : 0),
        month: Number(row?.month) || (Number.isFinite(parsedMonth) ? parsedMonth : 0),
        status: row?.status === 'Paid' ? 'Paid' : 'Unpaid'
      }
    })

    // Remove duplicates based on employee_id and period combination
    const seen = new Map()
    const deduplicatedRows = []

    for (const row of normalizedRows) {
      const key = `${row.employee_id}-${row.period}`
      if (!seen.has(key)) {
        seen.set(key, true)
        deduplicatedRows.push(row)
      } else {
        // Duplicate salary record removed
      }
    }

    return deduplicatedRows
  }

  const calculateSalaryRowTotalAmount = (row) => {
    const dailySalary = Number(row.employee_daily_salary || 0)
    const monthlySalary = Number(row.employee_monthly_salary || 0)
    const hourRate = Number(row.hour_rate || 0)
    const otRate = Number(row.ot_rate || 1.5)
    const weekendRate = Number(row.weekend_rate || 0)
    const publicHolidayRate = Number(row.public_holiday_rate || 0)
    const totalWorked = Number(row.total_days_worked || 0)
    const totalOtHours = Number(row.total_ot_hours || 0)
    const halfDayTotalHours = Number(row.half_day_total_hours || 0)
    const weekend = Number(row.weekend || 0)
    const publicHoliday = Number(row.public_holiday || 0)
    return Number((monthlySalary
      + (dailySalary * totalWorked)
      + (totalOtHours * hourRate * otRate)
      + (halfDayTotalHours * hourRate)
      + (weekend * weekendRate)
      + (publicHoliday * publicHolidayRate)).toFixed(2))
    const wdFullDays = Number(row.wd_full_days || 0)
    const weFullDays = Number(row.we_full_days || 0)
    const phFullDays = Number(row.ph_full_days || 0)
    const wdHalfDayHours = Number(row.wd_half_day_hours || 0)
    const weHalfDayHours = Number(row.we_half_day_hours || 0)
    const phHalfDayHours = Number(row.ph_half_day_hours || 0)
    const wdOtHours = Number(row.wd_ot_hours || 0)
    const weOtHours = Number(row.we_ot_hours || 0)
    const phOtHours = Number(row.ph_ot_hours || 0)

    const weekdayAmount = ((wdFullDays * 8 + wdHalfDayHours) * weekdayRate) + (wdOtHours * 1.5 * weekdayRate)
    const weekendAmount = position !== 'Worker'
      ? Number(row.weekend || 0) * weekendRate
      : ((weFullDays * 8 + weHalfDayHours) * weekendRate) + (weOtHours * 1.5 * weekendRate)
    const publicHolidayAmount = position !== 'Worker'
      ? Number(row.public_holiday || 0) * publicHolidayRate
      : ((phFullDays * 8 + phHalfDayHours) * publicHolidayRate) + (phOtHours * 1.5 * publicHolidayRate)
    const salaryBonus = !employeeUsesMonthlySalary
      ? (wdFullDays + weFullDays + phFullDays) * employeeSalary
      : 0

    return Number((weekdayAmount + weekendAmount + publicHolidayAmount + salaryBonus).toFixed(2))
  }

  const buildSalarySummaryFromAttendance = (attendanceRows, expenseRows = []) => {
    const grouped = new Map()

    attendanceRows.forEach((row) => {
      if (!row?.employee_id || !row?.submission_date) return

      const submissionDate = new Date(row.submission_date)
      if (Number.isNaN(submissionDate.getTime())) return

      const period = `${submissionDate.getFullYear()}-${String(submissionDate.getMonth() + 1).padStart(2, '0')}`
      const key = `${row.employee_id}-${period}`
      const year = submissionDate.getFullYear()
      const month = submissionDate.getMonth() + 1

      if (!grouped.has(key)) {
        grouped.set(key, {
          employee_id: row.employee_id,
          employee_name: row.Employee?.name || 'N/A',
          year,
          month,
          period,
          total_amount: 0,
          total_days_worked: 0,
          half_day_total_hours: 0,
          total_ot_hours: 0,
          weekend: 0,
          public_holiday: 0,
          wd_full_days: 0,
          we_full_days: 0,
          ph_full_days: 0,
          wd_half_day_hours: 0,
          we_half_day_hours: 0,
          ph_half_day_hours: 0,
          wd_ot_hours: 0,
          we_ot_hours: 0,
          ph_ot_hours: 0,
          employee_salary: Number(row.Employee?.monthly_salary || row.Employee?.daily_salary || 0),
          employee_monthly_salary: Number(row.Employee?.monthly_salary || 0),
          employee_daily_salary: Number(row.Employee?.daily_salary || 0),
          ot_rate: Number(row.Employee?.ot_rate || 1.5),
          hour_rate: Number(row.Employee?.hour_rate || 0),
          weekend_rate: Number(row.Employee?.weekend_rate || 0),
          public_holiday_rate: Number(row.Employee?.public_holiday_rate || 0),
          employee_position: row.Employee?.position || '',
          allowance: 0,
          employeer_epf: 0,
          total_advanced: 0,
          total_receipt: 0,
          salary_amount: 0,
          status: 'Unpaid'
        })
      }

      const item = grouped.get(key)
      const normalHours = Number(row.normal_hours || 0)
      const otHours = Number(row.ot_hours || 0)

      if (Number.isFinite(normalHours) && normalHours >= 8) {
        item.total_days_worked += 1
        if (row.type === 'WD') item.wd_full_days += 1
        if (row.type === 'WE') item.we_full_days += 1
        if (row.type === 'PH') item.ph_full_days += 1
      }

      if (Number.isFinite(normalHours) && normalHours < 8) {
        item.half_day_total_hours += normalHours
        if (row.type === 'WD') item.wd_half_day_hours += normalHours
        if (row.type === 'WE') item.we_half_day_hours += normalHours
        if (row.type === 'PH') item.ph_half_day_hours += normalHours
      }

      item.total_ot_hours += Number.isFinite(otHours) ? otHours : 0
      if (row.type === 'WD') item.wd_ot_hours += Number.isFinite(otHours) ? otHours : 0
      if (row.type === 'WE') item.we_ot_hours += Number.isFinite(otHours) ? otHours : 0
      if (row.type === 'PH') item.ph_ot_hours += Number.isFinite(otHours) ? otHours : 0

      if (row.type === 'WE') item.weekend += 1
      if (row.type === 'PH') item.public_holiday += 1
    })

    expenseRows.forEach((row) => {
      if (!row?.employee_id) return
      if (row?.status !== 'Approved') return

      const dateSource = row.expense_date
      if (!dateSource) return

      const dt = new Date(dateSource)
      if (Number.isNaN(dt.getTime())) return

      const period = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
      const key = `${row.employee_id}-${period}`
      if (!grouped.has(key)) return

      const amount = Number(row.amount || 0)
      if (!Number.isFinite(amount)) return

      const item = grouped.get(key)
      if (row.expense_type === 'Advanced') item.total_advanced += amount
      if (row.expense_type === 'Receipt') item.total_receipt += amount
    })

    const summary = Array.from(grouped.values()).map((item) => {
      const totalAmount = calculateSalaryRowTotalAmount(item)
      const allowance = Number(item.allowance || 0)
      const employeerEpf = Number(item.employeer_epf || 0)
      const totalAdvanced = Number(item.total_advanced || 0)
      const totalReceipt = Number(item.total_receipt || 0)
      const employeeSalary = Number(item.employee_salary || 0)
      const monthlySalary = Number(item.employee_monthly_salary || 0)
      const salaryAmount = Number((totalAmount + monthlySalary + allowance + employeerEpf) - (totalAdvanced - totalReceipt)).toFixed(2)

      return {
        ...item,
        total_amount: totalAmount,
        half_day_total_hours: Number(item.half_day_total_hours.toFixed(2)),
        total_ot_hours: Number(item.total_ot_hours.toFixed(2)),
        total_advanced: Number(totalAdvanced.toFixed(2)),
        total_receipt: totalReceipt,
        salary_amount: salaryAmount,
        status: item.status === 'Paid' ? 'Paid' : 'Unpaid'
      }
    })

    summary.sort((a, b) => {
      if (a.period === b.period) return a.employee_id - b.employee_id
      return b.period.localeCompare(a.period)
    })

    return summary
  }

  // Helper function to generate standard format numbers
  const generateSalaryNo = (index) => `SAL-${String(index + 1).padStart(4, '0')}`
  const generateAttendanceNo = (index) => `ATD-${String(index + 1).padStart(4, '0')}`
  const formatClockTime = (timeValue) => {
    if (!timeValue) return '-'
    return String(timeValue).slice(0, 5)
  }

  const formatDateForInput = (value) => {
    if (!value) return ''
    const dt = new Date(value)
    if (Number.isNaN(dt.getTime())) return ''
    const year = dt.getFullYear()
    const month = String(dt.getMonth() + 1).padStart(2, '0')
    const day = String(dt.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const toFixedNumber = (value, digits = 2) => {
    const num = Number(value)
    const safeNum = Number.isFinite(num) ? num : 0
    return safeNum.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
  }

  const hasAttendanceAttachment = (attachmentValue) => {
    if (!attachmentValue) return false
    if (Array.isArray(attachmentValue)) return attachmentValue.length > 0
    if (typeof attachmentValue === 'string') {
      const trimmed = attachmentValue.trim()
      if (!trimmed) return false
      try {
        const parsed = JSON.parse(trimmed)
        return Array.isArray(parsed) ? parsed.length > 0 : Boolean(parsed)
      } catch {
        return true
      }
    }
    if (typeof attachmentValue === 'object') {
      return Object.keys(attachmentValue).length > 0
    }
    return false
  }

  const handleEditAttendance = async (attendance) => {
    setSelectedAttendance(attendance)
    setAttendanceEditForm({
      project_id: attendance.project_id || '',
      po_id: attendance.po_id || '',
      submission_date: formatDateForInput(attendance.submission_date),
      clock_in: formatClockTime(attendance.clock_in) === '-' ? '08:00' : formatClockTime(attendance.clock_in),
      clock_out: formatClockTime(attendance.clock_out) === '-' ? '17:00' : formatClockTime(attendance.clock_out),
      normal_hours: attendance.normal_hours !== null && attendance.normal_hours !== undefined ? String(attendance.normal_hours) : '',
      ot_hours: attendance.ot_hours !== null && attendance.ot_hours !== undefined ? String(attendance.ot_hours) : '',
      type: attendance.type || 'WD',
      remarks: attendance.remarks || ''
    })
    setShowAttendanceEditModal(true)
  }

  const submitAttendanceEdit = async (e) => {
    e.preventDefault()
    if (!selectedAttendance) return

    try {
      setLoading(true)

      const updateData = {
        po_id: attendanceEditForm.po_id ? Number(attendanceEditForm.po_id) : null,
        submission_date: attendanceEditForm.submission_date || null,
        clock_in: `${attendanceEditForm.clock_in}:00`,
        clock_out: `${attendanceEditForm.clock_out}:00`,
        normal_hours: attendanceEditForm.normal_hours === '' ? 0 : Number(attendanceEditForm.normal_hours),
        ot_hours: attendanceEditForm.ot_hours === '' ? 0 : Number(attendanceEditForm.ot_hours),
        type: attendanceEditForm.type,
        remarks: attendanceEditForm.remarks
      }


      await attendanceService.update(selectedAttendance.attendance_id, updateData)
      setShowAttendanceEditModal(false)
      setSelectedAttendance(null)
      showMessage('Timesheet updated successfully!', 'Success')
      await fetchAttendances()
    } catch (err) {
      console.error('Error updating attendance:', err)
      showMessage(err.response?.data?.message || 'Failed to update timesheet', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const openSalaryEditModal = (salary) => {
    setSelectedSalaryRow(salary)
    setSalaryEditForm({
      allowance: Number(salary.allowance || 0),
      employeer_epf: Number(salary.employeer_epf || 0),
      remarks: salary.remarks || '',
      status: salary.status === 'Paid' ? 'Paid' : 'Unpaid'
    })
    setShowSalaryEditModal(true)
  }

  const submitSalaryEdit = async (e) => {
    e.preventDefault()
    if (!selectedSalaryRow) return

    try {
      setLoading(true)
      await salaryService.upsertSummaryMeta({
        employee_id: selectedSalaryRow.employee_id,
        period: selectedSalaryRow.period,
        allowance: Number(salaryEditForm.allowance || 0),
        employeer_epf: Number(salaryEditForm.employeer_epf || 0),
        remarks: salaryEditForm.remarks || '',
        status: salaryEditForm.status || 'Unpaid'
      })
      setShowSalaryEditModal(false)
      setSelectedSalaryRow(null)
      showMessage('Salary updated successfully!', 'Success')
      await fetchSalaries()
    } catch (err) {
      console.error('Error updating salary remarks:', err)
      showMessage(err.response?.data?.message || 'Failed to update salary remarks', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAttendance = async (attendanceId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this timesheet record?')
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      await attendanceService.delete(attendanceId)
      showMessage('Timesheet deleted successfully!', 'Success')
      await fetchAttendances()
    } catch (err) {
      console.error('Error deleting attendance:', err)
      showMessage(err.response?.data?.message || 'Failed to delete timesheet', 'Error')
    } finally {
      setLoading(false)
    }
  }

  // Helper function to determine day type (WD, WE, PH)
  const getDayType = (date) => {
    // For now, just use WD as default. This would need holiday data from backend
    const dayOfWeek = new Date(date).getDay()
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return 'WE' // Weekend
    }
    return 'WD' // Weekday
  }

  const resetSalaryFilters = () => {
    setSalaryFilters({
      years: [],
      months: [],
      employeeNames: [],
      statuses: []
    })
  }

  const resetAttendanceFilters = () => {
    setAttendanceFilters({
      years: [],
      months: [],
      employeeNames: [],
      projectIds: []
    })
  }

  // Task Assignment Helper Functions
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()

    // Get first day of month and total days
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()

    // Get the day of week for the first day (0 = Sunday, 6 = Saturday)
    const startDayOfWeek = firstDay.getDay()

    const days = []

    // Add days from previous month to fill the first week
    const prevMonth = new Date(year, month, 0)
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push(new Date(year, month - 1, prevMonth.getDate() - i))
    }

    // Add days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }

    // Add days from next month to complete the grid (42 cells = 6 weeks)
    const remainingCells = 42 - days.length
    for (let day = 1; day <= remainingCells; day++) {
      days.push(new Date(year, month + 1, day))
    }


    return days
  }

  const formatDateToLocal = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const handleDateClick = (date) => {
    setEditingAssignment(null) // Clear any existing edit
    setSelectedFromDate(date)
    setSelectedToDate(date)
    // Prefill modal from filter selections for convenience
    setModalTeam(selectedTeam || '')
    setModalPO(selectedPO || '')
    setShowBookingModal(true)
  }

  const handleEditAssignment = (assignment) => {
    // Validate assignment has proper ID
    if (!assignment || !assignment.id) {
      showMessage('Invalid assignment data', 'Error')
      return
    }

    setEditingAssignment(assignment)
    setSelectedFromDate(new Date(assignment.from_date))
    setSelectedToDate(new Date(assignment.to_date))
    setModalTeam(assignment.team)
    setModalPO(String(assignment.po_id))
    setShowBookingModal(true)
  }

  const handleBookSlot = async () => {
    if (!selectedFromDate || !selectedToDate || !modalTeam || !modalPO) {
      showMessage('Please select from date, to date, team, and PO', 'Validation')
      return
    }

    if (selectedToDate < selectedFromDate) {
      showMessage('To date must be after from date', 'Validation')
      return
    }

    try {
      setLoading(true)
      const fromDateStr = formatDateToLocal(selectedFromDate)
      const toDateStr = formatDateToLocal(selectedToDate)
      const poNumber = purchaseOrders.find(po => po.po_id === Number(modalPO))?.po_number || 'Unknown'

      const dateRange = fromDateStr === toDateStr
        ? formatDate(selectedFromDate)
        : `${formatDate(selectedFromDate)} - ${formatDate(selectedToDate)}`

      if (editingAssignment) {
        // Update existing assignment in database
        await taskAssignmentService.update(editingAssignment.id, {
          po_number: poNumber,
          po_id: Number(modalPO),
          team: modalTeam,
          from_date: fromDateStr,
          to_date: toDateStr
        })
        showMessage(`Successfully updated ${poNumber} assignment for ${modalTeam} from ${dateRange}`, 'Success')
      } else {
        // Create new assignment in database
        await taskAssignmentService.create({
          po_number: poNumber,
          po_id: Number(modalPO),
          team: modalTeam,
          from_date: fromDateStr,
          to_date: toDateStr
        })
        showMessage(`Successfully assigned ${poNumber} to ${modalTeam} from ${dateRange}`, 'Success')
      }

      // Reset form and clear filters to show the new assignment
      setShowBookingModal(false)
      setEditingAssignment(null)
      setModalTeam('')
      setModalPO('')
      setSelectedTeam('')
      setSelectedPO('')
      setSelectedFromDate(null)
      setSelectedToDate(null)

      // Refresh task assignments
      await fetchTaskAssignments()
    } catch (err) {
      console.error('Error booking slot:', err)
      showMessage(err.response?.data?.message || 'Failed to book time slot', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const availableSalaryYears = Array.from(new Set(salaries.map((row) => Number(row.year)).filter((value) => Number.isFinite(value) && value > 0))).sort((a, b) => b - a)
  const availableSalaryMonths = Array.from(new Set(salaries.map((row) => Number(row.month)).filter((value) => Number.isFinite(value) && value >= 1 && value <= 12))).sort((a, b) => a - b)
  const availableSalaryEmployeeNames = Array.from(new Set(salaries.map((row) => String(row.employee_name || '').trim()).filter(Boolean))).sort()
  const availableAttendanceEmployeeNames = Array.from(new Set(attendances.map((row) => String(row?.Employee?.name || '').trim()).filter(Boolean))).sort()
  const availableAttendanceProjects = projects.map((project) => ({
    value: String(project.project_id),
    label: `${project.po_no || project.project_code || 'N/A'} - ${project.project_name}`
  }))

  // Year / month of a timesheet record, derived from its submission date
  // (same source the salary summary groups by).
  const getAttendanceYearMonth = (attendance) => {
    const date = new Date(attendance?.submission_date)
    if (Number.isNaN(date.getTime())) return { year: '', month: '' }
    return { year: String(date.getFullYear()), month: String(date.getMonth() + 1) }
  }
  const availableAttendanceYears = Array.from(new Set(attendances.map((row) => getAttendanceYearMonth(row).year).filter(Boolean))).sort((a, b) => Number(b) - Number(a))
  const availableAttendanceMonths = Array.from(new Set(attendances.map((row) => Number(getAttendanceYearMonth(row).month)).filter((value) => Number.isFinite(value) && value >= 1 && value <= 12))).sort((a, b) => a - b)

  const filteredSalaries = salaries.filter((salary) => {
    const salaryYear = String(salary.year || '')
    const salaryMonth = String(salary.month || '')
    const salaryStatus = salary.status === 'Paid' ? 'Paid' : 'Unpaid'
    const employeeName = String(salary.employee_name || '').trim()
    const matchYear = salaryFilters.years.length === 0 || salaryFilters.years.includes(salaryYear)
    const matchMonth = salaryFilters.months.length === 0 || salaryFilters.months.includes(salaryMonth)
    const matchStatus = salaryFilters.statuses.length === 0 || salaryFilters.statuses.includes(salaryStatus)
    const matchEmployeeName = salaryFilters.employeeNames.length === 0 || salaryFilters.employeeNames.includes(employeeName)

    return matchYear && matchMonth && matchStatus && matchEmployeeName
  })

  const filteredAttendances = attendances.filter((attendance) => {
    const employeeName = String(attendance?.Employee?.name || '').trim()
    const projectId = String(attendance?.project_id || '')
    const { year: attendanceYear, month: attendanceMonth } = getAttendanceYearMonth(attendance)
    const matchYear = attendanceFilters.years.length === 0 || attendanceFilters.years.includes(attendanceYear)
    const matchMonth = attendanceFilters.months.length === 0 || attendanceFilters.months.includes(attendanceMonth)
    const matchEmployeeName = attendanceFilters.employeeNames.length === 0 || attendanceFilters.employeeNames.includes(employeeName)
    const matchProject = attendanceFilters.projectIds.length === 0 || attendanceFilters.projectIds.includes(projectId)

    return matchYear && matchMonth && matchEmployeeName && matchProject
  })

  const totalSalaryPages = Math.max(1, Math.ceil(filteredSalaries.length / salaryRowsPerPage))
  const paginatedSalaries = filteredSalaries.slice((salaryPage - 1) * salaryRowsPerPage, salaryPage * salaryRowsPerPage)
  const totalAttendancePages = Math.max(1, Math.ceil(filteredAttendances.length / attendanceRowsPerPage))
  const paginatedAttendances = filteredAttendances.slice((attendancePage - 1) * attendanceRowsPerPage, attendancePage * attendanceRowsPerPage)

  useEffect(() => {
    setSalaryPage((prev) => Math.min(prev, totalSalaryPages))
  }, [totalSalaryPages])

  useEffect(() => {
    setAttendancePage((prev) => Math.min(prev, totalAttendancePages))
  }, [totalAttendancePages])

  useEffect(() => {
    setSalaryPage(1)
  }, [salaryFilters, salaryRowsPerPage])

  useEffect(() => {
    setAttendancePage(1)
  }, [attendanceFilters, attendanceRowsPerPage])

  return (
    <div className="space-y-6">
      {/* Header and Share Button */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">Salary & Timesheet</h1>
        <button 
          onClick={() => {
            const formLink = `${window.location.origin}/timesheet-form`
            navigator.clipboard.writeText(formLink)
            showMessage('Timesheet form link copied to clipboard!\n\n' + formLink, 'Success')
          }}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition sm:w-auto"
          title="Share this link with employees to submit timesheet"
        >
          📋 Share Timesheet Form
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-0 border-b border-gray-300 bg-white rounded-t-lg">
        <button
          onClick={() => setActiveTab('attendance')}
          className={`px-6 py-3 font-medium transition ${
            activeTab === 'attendance'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          📅 Timesheet Records
        </button>
        <button
          onClick={() => setActiveTab('salary')}
          className={`px-6 py-3 font-medium transition ${
            activeTab === 'salary'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          💰 Salary Records
        </button>
        <button
          onClick={() => setActiveTab('task-assignment')}
          className={`px-6 py-3 font-medium transition ${
            activeTab === 'task-assignment'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          🎯 Task Assignment
        </button>
      </div>

      {/* Salary Table */}
      {activeTab === 'salary' && (
        <div>
          <CollapsibleFilters
            className="bg-white rounded-t-lg shadow border border-b-0 border-gray-200"
            contentClassName="p-4 border-t border-gray-200"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Year</label>
                <MultiSelectFilterDropdown
                  options={availableSalaryYears.map((yearValue) => String(yearValue))}
                  selectedValues={salaryFilters.years}
                  onChange={(years) => setSalaryFilters((prev) => ({ ...prev, years }))}
                  placeholder="All years"
                  emptyText="No years"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Month</label>
                <MultiSelectFilterDropdown
                  options={availableSalaryMonths.map((monthValue) => ({ value: String(monthValue), label: getMonthLabel(monthValue) }))}
                  selectedValues={salaryFilters.months}
                  onChange={(months) => setSalaryFilters((prev) => ({ ...prev, months }))}
                  placeholder="All months"
                  emptyText="No months"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee Name</label>
                <MultiSelectFilterDropdown
                  options={availableSalaryEmployeeNames}
                  selectedValues={salaryFilters.employeeNames}
                  onChange={(employeeNames) => setSalaryFilters((prev) => ({ ...prev, employeeNames }))}
                  placeholder="All employees"
                  emptyText="No employees"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
                <MultiSelectFilterDropdown
                  options={['Paid', 'Unpaid']}
                  selectedValues={salaryFilters.statuses}
                  onChange={(statuses) => setSalaryFilters((prev) => ({ ...prev, statuses }))}
                  placeholder="All status"
                  emptyText="No status options"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={resetSalaryFilters}
                  className="btn-reset-standard"
                >
                  🔄 Reset Filters
                </button>
              </div>
            </div>
          </CollapsibleFilters>
          {loading ? (
            <div className="bg-white rounded-b-lg shadow p-8 text-center">
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-gray-600">Loading salary records...</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-b-lg shadow p-8 text-center">
              <div className="text-4xl mb-4">⚠️</div>
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchSalaries}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
              >
                Retry
              </button>
            </div>
          ) : (
            <div ref={salaryTableRef} className="bg-white rounded-b-lg shadow overflow-x-auto overflow-y-auto max-h-[70vh]">
              <table className="w-full">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Salary No.</th>
                    <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Employee No.</th>
                    <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Employee Name</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Year</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Month</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Total Worked (Day)</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Total Half-Day (Hours)</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Total Overtime (Hours)</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Weekend (Day)</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Public Holiday (Day)</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Total Salary</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Allowance</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Employeer EPF</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Total Advanced</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Total Receipt</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Balance Salary</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Remarks</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Status</th>
                    {!isViewer && <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredSalaries.length === 0 ? (
                    <tr>
                      <td colSpan={isViewer ? '18' : '19'} className="px-3 py-8 text-center text-gray-500">
                        No salary records yet.
                      </td>
                    </tr>
                  ) : (
                    paginatedSalaries.map((salary, index) => {
                      const salaryIndex = (salaryPage - 1) * salaryRowsPerPage + index
                      const payrollStatus = salary.status === 'Paid' ? 'Paid' : 'Unpaid'
                      return (
                      <tr key={`${salary.employee_id}-${salary.period}-${salaryIndex}`} className="border-b hover:bg-gray-50" onClick={() => openSalaryEditModal(salary)}>
                        <td className="px-3 py-2 text-sm text-left font-medium whitespace-nowrap text-blue-600">{generateSalaryNo(salaryIndex)}</td>
                        <td className="px-3 py-2 text-sm text-left whitespace-nowrap">EMP-{String(salary.employee_id).padStart(5, '0')}</td>
                        <td className="px-3 py-2 text-sm text-left whitespace-nowrap"><TruncatedText text={salary.employee_name} fallback="N/A" /></td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{salary.year || '-'}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{getMonthLabel(salary.month)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{toFixedNumber(salary.total_days_worked, 0)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{toFixedNumber(salary.half_day_total_hours, 0)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{toFixedNumber(salary.total_ot_hours, 0)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{toFixedNumber(salary.weekend, 0)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{toFixedNumber(salary.public_holiday, 0)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{formatCurrency(salary.total_amount)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{formatCurrency(salary.allowance)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{formatCurrency(salary.employeer_epf)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{formatCurrency(salary.total_advanced)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{formatCurrency(salary.total_receipt)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap font-semibold text-gray-800">{formatCurrency(salary.salary_amount)}</td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap"><TruncatedText text={salary.remarks} /></td>
                        <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            payrollStatus === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {payrollStatus}
                          </span>
                        </td>
                        {!isViewer && (
                          <td className="px-3 py-2 text-center whitespace-nowrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                openSalaryEditModal(salary)
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                              title="Edit"
                            >
                              ✏️
                            </button>
                          </td>
                        )}
                      </tr>
                      )
                    })
                  )}
                </tbody>
                {filteredSalaries.length > 0 && (
                  <tfoot className="sticky bottom-0 bg-gray-100 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
                    <tr>
                      <td colSpan={15} className="px-3 py-2.5 text-right text-sm font-semibold text-gray-700 whitespace-nowrap">
                        Total ({filteredSalaries.length} record{filteredSalaries.length !== 1 ? 's' : ''}) — Total Amount:
                      </td>
                      <td className="px-3 py-2.5 text-center text-sm font-bold text-gray-900 whitespace-nowrap">
                        {formatCurrency(filteredSalaries.reduce((sum, salary) => sum + (Number(salary.salary_amount) || 0), 0))}
                      </td>
                      <td colSpan={isViewer ? 2 : 3} />
                    </tr>
                  </tfoot>
                )}
              </table>
                <TablePagination
                  currentPage={salaryPage}
                  totalPages={totalSalaryPages}
                  totalItems={filteredSalaries.length}
                  rowsPerPage={salaryRowsPerPage}
                  onPrevious={() => setSalaryPage((prev) => Math.max(prev - 1, 1))}
                  onNext={() => setSalaryPage((prev) => Math.min(prev + 1, totalSalaryPages))}
                />
            </div>
          )}
        </div>
      )}

      {/* Attendance Table */}
      {activeTab === 'attendance' && (
        <div>
          <CollapsibleFilters
            className="bg-white rounded-t-lg shadow border border-b-0 border-gray-200"
            contentClassName="p-4 border-t border-gray-200"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Year</label>
                <MultiSelectFilterDropdown
                  options={availableAttendanceYears.map((yearValue) => String(yearValue))}
                  selectedValues={attendanceFilters.years}
                  onChange={(years) => setAttendanceFilters((prev) => ({ ...prev, years }))}
                  placeholder="All years"
                  emptyText="No years"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Month</label>
                <MultiSelectFilterDropdown
                  options={availableAttendanceMonths.map((monthValue) => ({ value: String(monthValue), label: getMonthLabel(monthValue) }))}
                  selectedValues={attendanceFilters.months}
                  onChange={(months) => setAttendanceFilters((prev) => ({ ...prev, months }))}
                  placeholder="All months"
                  emptyText="No months"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee Name</label>
                <MultiSelectFilterDropdown
                  options={availableAttendanceEmployeeNames}
                  selectedValues={attendanceFilters.employeeNames}
                  onChange={(employeeNames) => setAttendanceFilters((prev) => ({ ...prev, employeeNames }))}
                  placeholder="All employees"
                  emptyText="No employees"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PO No.</label>
                <MultiSelectFilterDropdown
                  options={availableAttendanceProjects}
                  selectedValues={attendanceFilters.projectIds}
                  onChange={(projectIds) => setAttendanceFilters((prev) => ({ ...prev, projectIds }))}
                  placeholder="All PO numbers"
                  emptyText="No PO numbers"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={resetAttendanceFilters}
                  className="btn-reset-standard"
                >
                  🔄 Reset Filters
                </button>
              </div>
            </div>
          </CollapsibleFilters>
          {loading ? (
            <div className="bg-white rounded-b-lg shadow p-8 text-center">
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-gray-600">Loading timesheet records...</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-b-lg shadow p-8 text-center">
              <div className="text-4xl mb-4">⚠️</div>
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchAttendances}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
              >
                Retry
              </button>
            </div>
          ) : (
            <div ref={attendanceTableRef} className="bg-white rounded-b-lg shadow overflow-x-auto overflow-y-auto max-h-[70vh]">
              <table className="w-full">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Timesheet No.</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Employee Name</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Clock In</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Clock Out</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">PO No.</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Normal Hours</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Overtime Hours</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Total Hours</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Remarks</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Date Submission</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Type</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Attachment</th>
                    {!isViewer && <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendances.length === 0 ? (
                    <tr>
                      <td colSpan={isViewer ? '13' : '14'} className="px-3 py-8 text-center text-gray-500">
                        No timesheet records yet.
                      </td>
                    </tr>
                  ) : (
                    paginatedAttendances.map((attendance, index) => {
                      const attendanceIndex = (attendancePage - 1) * attendanceRowsPerPage + index
                      return (
                      <tr key={attendance.attendance_id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => handleEditAttendance(attendance)}>
                        <td className="px-3 py-2 text-center text-blue-600 text-sm font-medium whitespace-nowrap">{generateAttendanceNo(attendanceIndex)}</td>
                        <td className="px-3 py-2 text-center text-sm whitespace-nowrap"><TruncatedText text={attendance.Employee?.name} fallback="N/A" /></td>
                        <td className="px-3 py-2 text-center text-sm whitespace-nowrap">{formatClockTime(attendance.clock_in)}</td>
                        <td className="px-3 py-2 text-center text-sm whitespace-nowrap">{formatClockTime(attendance.clock_out)}</td>
                        <td className="px-3 py-2 text-center text-sm whitespace-nowrap">{attendance.PurchaseOrder?.po_number || attendance.Project?.po_no || attendance.Project?.project_code || '-'}</td>
                        <td className="px-3 py-2 text-center text-sm whitespace-nowrap">{toFixedNumber(attendance.normal_hours)}</td>
                        <td className="px-3 py-2 text-center text-sm whitespace-nowrap">{toFixedNumber(attendance.ot_hours)}</td>
                        <td className="px-3 py-2 text-center text-sm whitespace-nowrap">{toFixedNumber(attendance.hours_worked)}</td>
                        <td className="px-3 py-2 text-center text-sm whitespace-nowrap"><TruncatedText text={attendance.remarks} fallback="-" /></td>
                        <td className="px-3 py-2 text-center text-sm whitespace-nowrap">
                          {formatDate(attendance.submission_date)}
                        </td>
                        <td className="px-3 py-2 text-center text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            attendance.type === 'WD' 
                              ? 'bg-blue-100 text-blue-800'
                              : attendance.type === 'WE'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {attendance.type}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm whitespace-nowrap text-center">
                          {hasAttendanceAttachment(attendance.attachment) ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                openAttendanceFileViewer(attendance)
                              }}
                              className="text-lg leading-none text-blue-600 hover:text-blue-800 transition"
                              title="Open attachment"
                            >
                              🖼️
                            </button>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        {!isViewer && (
                          <td className="px-3 py-2 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleEditAttendance(attendance)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDeleteAttendance(attendance.attendance_id)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </td>
                        )}
                      </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
              <TablePagination
                currentPage={attendancePage}
                totalPages={totalAttendancePages}
                totalItems={filteredAttendances.length}
                rowsPerPage={attendanceRowsPerPage}
                onPrevious={() => setAttendancePage((prev) => Math.max(prev - 1, 1))}
                onNext={() => setAttendancePage((prev) => Math.min(prev + 1, totalAttendancePages))}
              />
            </div>
          )}
        </div>
      )}

      {showSalaryEditModal && selectedSalaryRow && renderModal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowSalaryEditModal(false)}>
          <div className="modal-panel-standard bg-white max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 className="text-xl font-bold">Edit Salary No. {selectedSalaryRow.employee_id}-{selectedSalaryRow.period}</h2>
              <button
                type="button"
                onClick={() => setShowSalaryEditModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <form onSubmit={submitSalaryEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Allowance (RM)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={salaryEditForm.allowance}
                  onChange={(e) => setSalaryEditForm((prev) => ({ ...prev, allowance: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employeer EPF (RM)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={salaryEditForm.employeer_epf}
                  onChange={(e) => setSalaryEditForm((prev) => ({ ...prev, employeer_epf: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={salaryEditForm.remarks}
                  onChange={(e) => setSalaryEditForm((prev) => ({ ...prev, remarks: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={salaryEditForm.status}
                  onChange={(e) => setSalaryEditForm((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Unpaid">Unpaid</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowSalaryEditModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" disabled={!canMutate} className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAttendanceEditModal && selectedAttendance && renderModal(
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowAttendanceEditModal(false)}>
          <div className="modal-panel-standard bg-white max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 className="text-xl font-bold">Edit Timesheet No. {selectedAttendance.attendance_code || selectedAttendance.attendance_id}</h2>
              <button
                type="button"
                onClick={() => setShowAttendanceEditModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <form onSubmit={submitAttendanceEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                <input value={selectedAttendance.Employee?.name || 'N/A'} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO No.</label>
                <select
                  value={attendanceEditForm.po_id}
                  onChange={(e) => setAttendanceEditForm((prev) => ({ ...prev, po_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">-- Select PO No. --</option>
                  {purchaseOrders.map((po) => (
                    <option key={po.po_id} value={po.po_id}>{po.po_number}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submission Date</label>
                <input
                  type="date"
                  value={attendanceEditForm.submission_date}
                  onChange={(e) => setAttendanceEditForm((prev) => ({ ...prev, submission_date: e.target.value }))}
                  className="modal-date-input w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Clock In</label>
                  <input type="time" value={attendanceEditForm.clock_in} onChange={(e) => setAttendanceEditForm((prev) => ({ ...prev, clock_in: e.target.value }))} className="modal-date-input w-full px-3 py-2 border border-gray-300 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Clock Out</label>
                  <input type="time" value={attendanceEditForm.clock_out} onChange={(e) => setAttendanceEditForm((prev) => ({ ...prev, clock_out: e.target.value }))} className="modal-date-input w-full px-3 py-2 border border-gray-300 rounded-lg" required />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Normal Hours</label>
                  <input
                    type="number"
                    value={attendanceEditForm.normal_hours}
                    onChange={(e) => setAttendanceEditForm((prev) => ({ ...prev, normal_hours: e.target.value }))}
                    min="0"
                    step="0.01"
                    placeholder="e.g. 8"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="mt-1 text-xs text-gray-500">Editable — overrides the value derived from clock times.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">OT Hours</label>
                  <input
                    type="number"
                    value={attendanceEditForm.ot_hours}
                    onChange={(e) => setAttendanceEditForm((prev) => ({ ...prev, ot_hours: e.target.value }))}
                    min="0"
                    step="0.01"
                    placeholder="e.g. 1.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="mt-1 text-xs text-gray-500">Editable — overrides the value derived from clock times.</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={attendanceEditForm.type} onChange={(e) => setAttendanceEditForm((prev) => ({ ...prev, type: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="WD">WD</option>
                  <option value="WE">WE</option>
                  <option value="PH">PH</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={attendanceEditForm.remarks}
                  onChange={(e) => setAttendanceEditForm((prev) => ({ ...prev, remarks: e.target.value }))}
                  rows={3}
                  placeholder="Optional notes or comments..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attachment</label>
                {selectedAttendance && (() => {
                  const attachments = parseAttachmentList(selectedAttendance.attachment)
                  const attachmentUrl = attachments.length ? resolveAttachmentUrl(attachments[0]) : ''
                  const attachmentExt = getFileExtension(attachmentUrl)
                  if (!attachmentUrl || !['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(attachmentExt)) {
                    return null
                  }
                  return (
                    <button type="button" onClick={() => openAttendanceFileViewer(selectedAttendance)} className="block w-full overflow-hidden rounded-lg border border-gray-200 bg-white hover:border-blue-300 transition">
                      <img src={attachmentUrl} alt="Attachment preview" className="w-full object-contain" />
                    </button>
                  )
                })()}
              </div>
              <div className="flex gap-3 pt-6 border-t mt-6">
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" disabled={loading || !canMutate}>
                  {loading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewingAttendanceFile && renderModal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closeAttendanceFileViewer}>
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-screen overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b bg-gray-50">
              <h2 className="text-lg font-bold text-gray-800">Attachment Preview</h2>
              <button
                type="button"
                onClick={closeAttendanceFileViewer}
                className="modal-close-button"
                aria-label="Close viewer"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-100 p-4">
              {['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(getFileExtension(viewingAttendanceFile)) ? (
                <img
                  src={viewingAttendanceFile}
                  alt="Attendance attachment"
                  className="max-w-full max-h-full object-contain"
                />
              ) : ['pdf'].includes(getFileExtension(viewingAttendanceFile)) ? (
                <iframe
                  src={viewingAttendanceFile}
                  title="Attachment Preview"
                  className="w-full h-full border-none"
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-600">
                  <div className="text-6xl mb-4">📄</div>
                  <p className="text-lg mb-2">Cannot preview this attachment</p>
                  <button
                    type="button"
                    onClick={() => downloadFile(viewingAttendanceFile, 'attachment')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg"
                  >
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Task Assignment Tab */}
      {activeTab === 'task-assignment' && (
        <div className="space-y-6">
          {/* Task Assignment Header */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">🎯 Task Assignment</h2>
            <p className="text-gray-600 mb-6">
              Assign teams to Purchase Orders and book time slots. These assignments will automatically populate the PO No. field in timesheet records.
            </p>

            {/* Assignment Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Team</label>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="">All Teams</option>
                  {Array.from(new Set(employees.map(emp => emp.team).filter(Boolean))).sort().map(team => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select PO No.</label>
                <select
                  value={selectedPO}
                  onChange={(e) => setSelectedPO(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="">All POs</option>
                  {purchaseOrders.map(po => (
                    <option key={po.po_id} value={po.po_id}>{po.po_number}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">View</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentView('month')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                      currentView === 'month'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Month
                  </button>
                  <button
                    onClick={() => setCurrentView('week')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                      currentView === 'week'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Week
                  </button>
                </div>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSelectedTeam('')
                    setSelectedPO('')
                    setCurrentDate(new Date())
                  }}
                  className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition"
                >
                  🔄 Reset Filters
                </button>
              </div>
            </div>
          </div>

          {/* Calendar View */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition"
                >
                  ◀ Previous
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                >
                  Today
                </button>
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition"
                >
                  Next ▶
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-semibold text-gray-700 py-2">
                  {day}
                </div>
              ))}

              {/* Calendar Days */}
              {generateCalendarDays().map((date, index) => {
                const dateStr = formatDateToLocal(date)
                const selectedPONumber = selectedPO ? purchaseOrders.find(po => po.po_id === Number(selectedPO))?.po_number : null
                const dayAssignments = taskAssignments.filter(
                  assignment => dateStr >= assignment.from_date && dateStr <= assignment.to_date &&
                  (!selectedTeam || assignment.team === selectedTeam) &&
                  (!selectedPONumber || assignment.po_number === selectedPONumber)
                )

                const isToday = date.toDateString() === new Date().toDateString()
                const isCurrentMonth = date.getMonth() === currentDate.getMonth()

                return (
                  <div
                    key={index}
                    onClick={() => handleDateClick(date)}
                    className={`min-h-32 p-1 border rounded-lg cursor-pointer transition ${
                      isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                    } ${!isCurrentMonth ? 'opacity-50' : ''}`}
                  >
                    <div className="text-sm font-medium text-gray-700 mb-1 text-center">
                      {date.getDate()}
                    </div>

                    {/* Google Calendar-style Event Labels */}
                    <div className="space-y-1">
                      {dayAssignments.map((assignment, idx) => {
                        // Generate consistent color based on PO number
                        const getPOColor = (poNumber) => {
                          const colors = [
                            { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
                            { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
                            { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
                            { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
                            { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300' },
                            { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300' },
                            { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
                            { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
                          ]
                          const hash = poNumber.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
                          return colors[hash % colors.length]
                        }

                        const poColor = getPOColor(assignment.po_number)

                        return (
                        <div
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditAssignment(assignment)
                          }}
                          className={`text-xs px-2 py-1 rounded ${poColor.bg} ${poColor.text} truncate font-medium border-l-4 ${poColor.border} cursor-pointer hover:opacity-80 transition`}
                          title={`${assignment.team} - ${assignment.po_number}\n${formatDate(assignment.from_date)} - ${formatDate(assignment.to_date)}\nClick to edit or remove`}
                        >
                          {assignment.po_number}
                        </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Booking Modal */}
          {showBookingModal && selectedFromDate && renderModal(
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowBookingModal(false)}>
              <div className="modal-panel-standard bg-white max-w-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="mb-6 flex items-start justify-between gap-3">
                  <h2 className="text-2xl font-bold">{editingAssignment ? 'Edit PO Assignment' : 'Book Time Slot'}</h2>
                  <button
                    type="button"
                    onClick={() => setShowBookingModal(false)}
                    className="modal-close-button"
                    aria-label="Close modal"
                  >
                    ×
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600">
                    <strong>Date Range:</strong> {selectedFromDate && formatDate(selectedFromDate)} {selectedToDate && selectedToDate !== selectedFromDate ? `- ${formatDate(selectedToDate)}` : ''}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                    <input
                      type="date"
                      value={selectedFromDate ? formatDateToLocal(selectedFromDate) : ''}
                      onChange={(e) => setSelectedFromDate(e.target.value ? new Date(e.target.value) : null)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                    <input
                      type="date"
                      value={selectedToDate ? formatDateToLocal(selectedToDate) : ''}
                      onChange={(e) => setSelectedToDate(e.target.value ? new Date(e.target.value) : null)}
                      min={selectedFromDate ? formatDateToLocal(selectedFromDate) : ''}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Team</label>
                    <select
                      value={modalTeam}
                      onChange={(e) => setModalTeam(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    >
                      <option value="">Select Team</option>
                      {Array.from(new Set(employees.map(emp => emp.team).filter(Boolean))).sort().map(team => (
                        <option key={team} value={team}>{team}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Order</label>
                    <select
                      value={modalPO}
                      onChange={(e) => setModalPO(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    >
                      <option value="">Select PO</option>
                      {purchaseOrders.map(po => (
                        <option key={po.po_id} value={po.po_id}>{po.po_number}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-3">
                  {editingAssignment && (
                    <button
                      onClick={async () => {
                        if (!editingAssignment || !editingAssignment.id) {
                          showMessage('Invalid assignment data', 'Error')
                          return
                        }

                        try {
                          setLoading(true)
                          // Remove assignment from database
                          await taskAssignmentService.delete(editingAssignment.id)

                          setShowBookingModal(false)
                          showMessage(`Assignment ${editingAssignment.po_number} removed successfully`, 'Success')

                          // Refresh task assignments from database
                          await fetchTaskAssignments()
                        } catch (error) {
                          console.error('Error removing assignment:', error)
                          showMessage(error.response?.data?.message || 'Failed to remove assignment', 'Error')
                        } finally {
                          setLoading(false)
                        }
                      }}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition"
                    >
                      🗑️ Remove
                    </button>
                  )}
                  <button
                    onClick={handleBookSlot}
                    disabled={!selectedFromDate || !selectedToDate || !modalTeam || modalPO === '' || modalPO === null || modalPO === undefined}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    🎯 {editingAssignment ? 'Update Assignment' : 'Book Slot'}
                  </button>
                  <button
                    onClick={() => setShowBookingModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg font-medium transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {modalElement}
    </div>
    )
  }
